import { logEvent, getClientUid } from "./logger";

const SAFE = new Set([
  "title", "book_id", "book", "book_size_id", "book_format", "pages_count",
  "width_mm", "height_mm", "page_padding", "cell_gap", "cell_radius", "in_cart",
  "cover_template", "cover_text", "cover_text_position", "cover_title_color",
  "cover_title_size", "cover_title_break", "cover_year", "cover_accent_color",
  "cover_bg_color", "cover_date_mode", "cover_date_from", "cover_date_to",
  "front_cover_layout", "back_cover_layout", "front_cover_photo_shape",
  "back_cover_photo_shape", "preset_id", "left_layout_id", "right_layout_id",
  "order", "orders", "spread_ids", "items", "id", "quantity", "side", "slot_index",
  "grid_index", "fit", "position", "crop_data", "crop_version", "quality",
  "include_bleed", "color_space", "filename",
]);
const CROP = new Set([
  "x", "y", "width", "height", "offset_x", "offset_y", "scale", "rotation",
  "crop_px_w", "crop_px_h", "natural_w", "natural_h", "upscale", "crop_box",
]);
export function safeEditorData(data, keys = SAFE) {
  if (typeof data === "string") {
    try { data = JSON.parse(data); } catch (_) { return {}; }
  }
  if (!data || typeof data !== "object") return {};
  const result = {};
  const entries = typeof FormData !== "undefined" && data instanceof FormData
    ? [...data.entries()] : Object.entries(data);
  for (const [key, value] of entries) {
    if (!keys.has(key)) continue;
    if (typeof File !== "undefined" && value instanceof File) continue;
    if (Array.isArray(value)) {
      result[key] = value.slice(0, 200).map(v => v && typeof v === "object"
        ? safeEditorData(v, keys) : v).filter(v => v == null || ["string", "number", "boolean", "object"].includes(typeof v));
      if (value.length > 200) result[key + "_truncated"] = true;
    } else if (value && typeof value === "object") {
      result[key] = safeEditorData(value, ["crop_data", "crop_box"].includes(key) ? CROP : keys);
    } else if (value == null || ["string", "number", "boolean"].includes(typeof value)) {
      result[key] = value;
    }
  }
  return result;
}
function newId() {
  const cryptoApi = window.crypto;

  if (cryptoApi.randomUUID) {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;

  const h = [...bytes]
    .map(v => v.toString(16).padStart(2, "0"))
    .join("");

  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function pathBookId(url) {
  return String(url || "").match(/\/books\/([0-9a-f-]{36})(?:\/|$)/i)?.[1] || null;
}
export function beginEditorRequest(config) {
  if (!["post", "put", "patch", "delete"].includes(String(config.method).toLowerCase())) return null;
  if (String(config.url).includes("/logs/")) return null;
  const requestId = newId();
  config.headers = config.headers || {};
  config.headers["X-Request-Id"] = requestId;
  config.headers["X-Client-Uid"] = getClientUid();
  const requested = safeEditorData(config.data);
  const bookId = pathBookId(config.url) || requested.book_id || null;
  const meta = { request_id: requestId, requested, book_id: bookId };
  logEvent({ source: "network", event: "editor.request.sent",
    message: `${String(config.method).toUpperCase()} ${config.url}: отправлено`,
    url: config.url, book_id: bookId, payload: meta });
  return meta;
}
export function finishEditorRequest(config, response, failed = false) {
  const meta = config?.metadata?.editorAudit;
  if (!meta) return;
  const serverId = response?.headers?.["x-request-id"] || null;
  logEvent({ source: "network", level: failed ? "warn" : "info",
    event: failed ? "editor.request.failed" : "editor.request.acknowledged",
    message: `${String(config.method).toUpperCase()} ${config.url}: ${failed ? "ошибка" : "ответ сервера"}`,
    url: config.url, book_id: meta.book_id,
    payload: { ...meta, status: response?.status || null, server_request_id: serverId,
      outcome: response ? "http_response" : "unknown_network_outcome" } });
}

// Для оставшихся POST через fetch (cart/validate, расчёт тарифа).
export async function auditFetch(url, init = {}) {
  const config = { url, method: init.method || "GET", data: init.body,
    headers: Object.fromEntries(new Headers(init.headers || {}).entries()), metadata: {} };
  config.metadata.editorAudit = beginEditorRequest(config);
  try {
    const response = await fetch(url, { ...init, headers: config.headers });
    finishEditorRequest(config, { status: response.status,
      headers: { "x-request-id": response.headers.get("X-Request-Id") } }, !response.ok);
    return response;
  } catch (error) {
    finishEditorRequest(config, null, true);
    throw error;
  }
}